import type { OAuthProviderId } from '@authsemply/shared';

/**
 * Racine de l'API du portail.
 *
 * En développement, un repli sur localhost. Partout ailleurs, la variable est
 * OBLIGATOIRE — et son absence est une erreur bruyante, pas un repli.
 *
 * L'ancien comportement retombait sur l'API de production : choisi pour qu'un
 * build mal configuré n'envoie jamais personne vers une machine de
 * développement, il faisait qu'un bundle de PRÉPROD parlait silencieusement à
 * la production. Le refus CORS l'a arrêté ; sans lui, des inscriptions de test
 * auraient créé de vrais comptes.
 *
 * Une page blanche en préprod se répare en une minute. Des écritures dans
 * l'annuaire de production ne se reprennent pas.
 *
 * `vite.config.ts` refuse déjà de construire sans cette variable : ce contrôle
 * est le second filet, pour un bundle assemblé autrement.
 */
function resolveApiUrl(): string {
  const configured = (import.meta.env.VITE_API_URL as string | undefined)?.trim();
  if (configured) return configured.replace(/\/$/, '');
  if (import.meta.env.DEV) return 'http://localhost:4100';

  throw new Error(
    'VITE_API_URL absente de ce build. Reconstruisez le portail avec la racine ' +
      "d'API de l'environnement visé — aucune valeur n'est devinée ici.",
  );
}

export const API_URL = resolveApiUrl();

export class ApiError extends Error {
  constructor(
    public readonly code: string,
    message: string,
    public readonly status: number,
    /** Corps complet de la réponse d'erreur (champs en cause, produits…). */
    public readonly details?: Record<string, unknown>,
  ) {
    super(message);
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const response = await fetch(`${API_URL}${path}`, {
    ...init,
    // Cookie de session SSO : host-only sur l'API depuis le 26/09/2026 (plus
    // sur tout .semply.fr). `include` le joint aux appels vers l'API, qui est
    // le seul hôte à le lire.
    credentials: 'include',
    headers: { 'content-type': 'application/json', ...(init.headers ?? {}) },
  });

  if (response.status === 204) return undefined as T;

  const payload = (await response.json().catch(() => ({}))) as Record<string, unknown>;

  if (!response.ok) {
    throw new ApiError(
      (payload.error as string) ?? 'server_error',
      (payload.message as string) ?? 'Une erreur est survenue',
      response.status,
      payload,
    );
  }

  return payload as T;
}

export const api = {
  get: <T>(path: string) => request<T>(path),
  post: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'POST', body: body ? JSON.stringify(body) : undefined }),
  patch: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PATCH', body: body ? JSON.stringify(body) : undefined }),
  put: <T>(path: string, body?: unknown) =>
    request<T>(path, { method: 'PUT', body: body ? JSON.stringify(body) : undefined }),
  delete: <T>(path: string) => request<T>(path, { method: 'DELETE' }),
  /** Départ vers un fournisseur externe (Google, Microsoft, Apple) : une navigation, pas un fetch. */
  oauthUrl: (provider: OAuthProviderId, returnTo?: string, reconnexion = false) => {
    const params = new URLSearchParams();
    if (returnTo) params.set('return_to', returnTo);
    // Reconnexion exigée par un produit : l'API lie la tentative au compte de
    // la session et demande au fournisseur une authentification fraîche.
    if (reconnexion) params.set('reconnexion', '1');
    const query = params.toString();
    return `${API_URL}/oauth/${provider}${query ? `?${query}` : ''}`;
  },
};

/**
 * Reconstruit l'URL /oidc/authorize à partir des paramètres transmis par l'API
 * quand elle a renvoyé l'utilisateur sur le portail. L'URL n'est jamais reprise
 * telle quelle depuis la barre d'adresse : seuls les paramètres connus sont
 * recopiés, sur l'origine de l'API issue de la configuration. Un lien forgé ne
 * peut donc pas faire du portail un redirecteur ouvert.
 *
 * `prompt` est volontairement omis : conserver `prompt=login` après une
 * connexion réussie renverrait l'utilisateur en boucle sur l'écran de connexion.
 */
const AUTHORIZE_PARAMS = [
  'client_id',
  'redirect_uri',
  'response_type',
  'scope',
  'state',
  'nonce',
  'code_challenge',
  'code_challenge_method',
  'login_hint',
] as const;

export function resumeAuthorizeUrl(params: URLSearchParams): string | null {
  if (!params.get('client_id') || !params.get('redirect_uri')) return null;

  const out = new URLSearchParams();
  for (const key of AUTHORIZE_PARAMS) {
    const value = params.get(key);
    if (value) out.set(key, value);
  }
  return `${API_URL}/oidc/authorize?${out.toString()}`;
}
